import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { EmptyState, ErrorState } from "@/components/StateViews";
import { toast } from "sonner";
import { Loader2, Handshake, Check, X, Send } from "lucide-react";

const STATUS_META = {
  pending_admin: { label: "بانتظار اعتماد الإدارة", tone: "bg-amber-50 text-amber-700 border-amber-100" },
  student_review: { label: "بانتظار موافقة الطالب", tone: "bg-blue-50 text-blue-700 border-blue-100" },
  approved: { label: "موافَق عليه", tone: "bg-emerald-50 text-emerald-700 border-emerald-100" },
  rejected: { label: "مرفوض", tone: "bg-red-50 text-red-700 border-red-100" },
};

export default function ContactRequestsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [noteFor, setNoteFor] = useState(null); // { req, action }
  const [note, setNote] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/contact-requests");
      setItems(data.requests || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, action, admin_note = null) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/contact-requests/${id}`, { action, admin_note });
      toast.success(action === "forward" ? "تم إرسال الطلب للطالب" : "تم رفض الطلب");
      setNoteFor(null); setNote("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(""); }
  };

  const pending = items.filter((r) => r.status === "pending_admin");
  const others = items.filter((r) => r.status !== "pending_admin");

  return (
    <DashboardLayout>
      <div data-testid="admin-contact-requests">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">وسيط التواصل</div>
          <h1 className="mt-2 text-2xl font-heading font-extrabold text-slate-900">طلبات تواصل جهات الاستقطاب</h1>
          <p className="mt-2 text-slate-600 max-w-3xl">
            كل طلب تواصل يمر عبر الإدارة قبل أن يصل للطالب. راجع الطلب، ثم مرّره للطالب لأخذ موافقته الصريحة، أو ارفضه إذا لم يكن ملائمًا.
          </p>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : error ? (
          <ErrorState message={error} onRetry={load} testid="admin-cr-error" />
        ) : items.length === 0 ? (
          <EmptyState icon={Handshake} title="لا توجد طلبات تواصل حالياً" description="ستظهر الطلبات هنا فور إرسالها من جهات الاستقطاب." testid="admin-cr-empty" />
        ) : (
          <div className="space-y-8">
            <Section title="بانتظار الاعتماد" count={pending.length}>
              {pending.length === 0 ? (
                <div className="text-sm text-slate-500 py-4">لا توجد طلبات بانتظار الاعتماد.</div>
              ) : (
                <div className="grid gap-3">
                  {pending.map((r) => (
                    <RequestCard key={r.id} r={r} busy={busyId === r.id} onForward={() => { setNoteFor({ req: r, action: "forward" }); setNote(""); }} onReject={() => { setNoteFor({ req: r, action: "reject" }); setNote(""); }} />
                  ))}
                </div>
              )}
            </Section>

            <Section title="أرشيف الطلبات" count={others.length}>
              {others.length === 0 ? (
                <div className="text-sm text-slate-500 py-4">لا يوجد طلبات سابقة.</div>
              ) : (
                <div className="grid gap-3">
                  {others.map((r) => (
                    <RequestCard key={r.id} r={r} readOnly />
                  ))}
                </div>
              )}
            </Section>
          </div>
        )}

        {noteFor && (
          <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) setNoteFor(null); }}>
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200" data-testid="admin-cr-note-modal">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                <h3 className="font-heading font-bold text-slate-900 text-lg">
                  {noteFor.action === "forward" ? "تمرير الطلب للطالب" : "رفض الطلب"}
                </h3>
                <button onClick={() => setNoteFor(null)} className="p-1.5 rounded-lg hover:bg-slate-100" aria-label="إغلاق"><X className="h-5 w-5" /></button>
              </div>
              <div className="p-4 space-y-4">
                <div className="text-sm text-slate-600">
                  {noteFor.action === "forward"
                    ? `سيصل الطلب للطالب «${noteFor.req.student_name}» ليقبل أو يرفض مشاركة بيانات التواصل.`
                    : `سيتم إبلاغ جهة «${noteFor.req.recruiter_name}» أن الطلب مرفوض.`}
                </div>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="ملاحظة (اختيارية)" className="w-full rounded-lg border border-slate-300 px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="admin-cr-note" />
                <div className="flex justify-end gap-2">
                  <button onClick={() => setNoteFor(null)} className="px-4 py-2 rounded-lg text-slate-700 hover:bg-slate-100">إلغاء</button>
                  <button
                    onClick={() => decide(noteFor.req.id, noteFor.action, note)}
                    disabled={busyId === noteFor.req.id}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-semibold disabled:opacity-60 ${noteFor.action === "forward" ? "bg-teal-700 hover:bg-teal-600" : "bg-red-600 hover:bg-red-500"}`}
                    data-testid="admin-cr-confirm"
                  >
                    {busyId === noteFor.req.id ? <Loader2 className="h-4 w-4 animate-spin" /> : noteFor.action === "forward" ? <Send className="h-4 w-4" /> : <X className="h-4 w-4" />}
                    {noteFor.action === "forward" ? "تمرير الآن" : "رفض الطلب"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Section({ title, count, children }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-lg font-heading font-bold text-slate-900">{title}</h2>
        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold arabic-numerals">{count}</span>
      </div>
      {children}
    </div>
  );
}

function RequestCard({ r, busy, onForward, onReject, readOnly }) {
  const meta = STATUS_META[r.status] || { label: r.status, tone: "bg-slate-100 text-slate-700 border-slate-200" };
  return (
    <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid={`admin-cr-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs text-slate-500">جهة الاستقطاب</div>
          <div className="font-heading font-bold text-slate-900">{r.recruiter_name}</div>
          <div className="text-xs text-slate-500 mt-0.5" dir="ltr">{r.recruiter_email}</div>
        </div>
        <div className="text-end">
          <div className="text-xs text-slate-500">الطالب المستهدَف</div>
          <div className="font-heading font-bold text-slate-900">{r.student_name}</div>
        </div>
        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold border ${meta.tone}`}>{meta.label}</span>
      </div>
      <div className="mt-3 p-3 rounded-lg bg-cream-50 text-sm text-slate-700 whitespace-pre-wrap">
        {r.message || "—"}
      </div>
      {r.admin_note && (
        <div className="mt-2 text-xs text-slate-500">ملاحظة الإدارة: <span className="text-slate-700">{r.admin_note}</span></div>
      )}
      {r.student_response && (
        <div className="mt-1 text-xs text-slate-500">رد الطالب: <span className="text-slate-700">{r.student_response}</span></div>
      )}
      {!readOnly && (
        <div className="mt-4 flex items-center gap-2">
          <button onClick={onForward} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid={`admin-cr-forward-${r.id}`}>
            <Send className="h-4 w-4" /> تمرير للطالب
          </button>
          <button onClick={onReject} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-50 text-red-700 text-sm font-semibold hover:bg-red-100 disabled:opacity-60" data-testid={`admin-cr-reject-${r.id}`}>
            <X className="h-4 w-4" /> رفض
          </button>
        </div>
      )}
    </div>
  );
}
